import {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useLocalStorage } from '@monofront/business/hooks';
import { useAuthentication } from '@context/businessContainer';
import { LEGAL_AGE } from '@constants/user';
import { updateBirthDate } from '@services/user';
import { toast } from 'react-toastify';
import { useTranslations } from 'next-intl';
import useI18n from '@hooks/i18n/useI18n';

interface IAlcoholEligibility {
  isAgeVerificationModalOpen: boolean;
  incrementProductCount: () => void;
  shouldIneligibilityToastBeTriggered: boolean;
  canBuyProduct: boolean;
  birthDate: string;
}

type AlcoholEligibilityAction =
  | {
      type: 'checkAlcoholEligibility';
      incrementProductCount: () => void;
    }
  | { type: 'closeAgeVerificationModal' }
  | { type: 'triggerIneligibilityToast' }
  | { type: 'resetToast' }
  | { type: 'canBuyProduct' }
  | { type: 'setBirthDate'; birthDate: string };

interface IAlcoholEligibilityProviderProps {
  children?: React.ReactNode;
}

type AlcoholEligibilityContextType = {
  updateUserBirthDate: (birthDate: string) => Promise<boolean>;
  alcoholEligibilityState: IAlcoholEligibility;
  dispatchAlcoholEligibility: Dispatch<AlcoholEligibilityAction>;
  userBirthDate: string | null | undefined;
  hasAlcoholOptIntBeenTriggered: (
    productSubType: string,
    incrementProductCount: () => void,
  ) => boolean;
  isUserEligibleToBuyAlcohol: () => boolean;
};

const initialAlcoholEligibilityState: IAlcoholEligibility = {
  isAgeVerificationModalOpen: false,
  incrementProductCount: () => null,
  shouldIneligibilityToastBeTriggered: false,
  canBuyProduct: true,
  birthDate: '',
};

const alcoholEligibilityReducer = (
  state: IAlcoholEligibility,
  action: AlcoholEligibilityAction,
) => {
  switch (action.type) {
    case 'checkAlcoholEligibility': {
      return {
        ...state,
        canBuyProduct: false,
        isAgeVerificationModalOpen: true,
        incrementProductCount: action.incrementProductCount,
      };
    }
    case 'closeAgeVerificationModal': {
      return {
        ...state,
        isAgeVerificationModalOpen: false,
      };
    }
    case 'triggerIneligibilityToast': {
      return {
        ...state,
        shouldIneligibilityToastBeTriggered: true,
      };
    }
    case 'resetToast': {
      return {
        ...state,
        shouldIneligibilityToastBeTriggered: false,
      };
    }
    case 'canBuyProduct': {
      return {
        ...state,
        canBuyProduct: true,
      };
    }
    case 'setBirthDate': {
      return {
        ...state,
        birthDate: action.birthDate,
      };
    }
    default:
      return state;
  }
};

const AlcoholEligibilityContext =
  createContext<AlcoholEligibilityContextType | null>(null);

export const AlcoholEligibilityProvider: React.FC<
  IAlcoholEligibilityProviderProps
> = ({ children }) => {
  const { isAuthenticated, authUser, customer } = useAuthentication();
  const [birthDate, setBirthDate] = useLocalStorage<string | null>(
    'birthDate',
    null,
  );
  const userBirthDate = isAuthenticated ? customer?.birthDate : birthDate;
  const [alcoholEligibilityState, dispatchAlcoholEligibility] = useReducer(
    alcoholEligibilityReducer,
    initialAlcoholEligibilityState,
  );

  const { isGermanMarket } = useI18n();
  const tErrors = useTranslations('Errors');

  useEffect(() => {
    if (!alcoholEligibilityState.shouldIneligibilityToastBeTriggered) {
      return;
    }

    toast(tErrors('alcoholIneligibilityError'), {
      progressClassName: 'toast-error',
    });
    dispatchAlcoholEligibility({ type: 'resetToast' });
  }, [alcoholEligibilityState.shouldIneligibilityToastBeTriggered]);

  const isUserAboveLegalAge = useCallback((birthDate: string) => {
    const date = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();

    const isBeforeBirthdayThisYear =
      today.getMonth() < date.getMonth() ||
      (today.getMonth() === date.getMonth() &&
        today.getDate() < date.getDate());

    if (isBeforeBirthdayThisYear) {
      age--;
    }

    return age >= LEGAL_AGE;
  }, []);

  const isAlcoholOptInRequired = useCallback(
    (productSubType: string) => {
      return (
        isGermanMarket &&
        productSubType === 'Alkoholische Getränke' &&
        !isUserAboveLegalAge(userBirthDate ?? '')
      );
    },
    [isGermanMarket, userBirthDate, isUserAboveLegalAge],
  );

  const isUserEligibleToBuyAlcohol = useCallback(() => {
    if (!isAuthenticated && birthDate) {
      return isUserAboveLegalAge(birthDate);
    }

    if (customer?.birthDate) {
      return isUserAboveLegalAge(customer.birthDate);
    }

    return false;
  }, [
    isAuthenticated,
    birthDate,
    customer?.birthDate,
    isUserAboveLegalAge,
    alcoholEligibilityState.birthDate,
  ]);

  const updateUserBirthDate = useCallback(
    async (birthDate: string) => {
      const [day, month, year] = birthDate
        .replaceAll('.', '/')
        .split('/')
        .map(Number);
      const date = new Date(year, month - 1, day);

      if (isAuthenticated) {
        const ageUpdateResponse = await updateBirthDate(
          date.toDateString(),
          authUser?.uid,
        );

        return ageUpdateResponse;
      }
      setBirthDate(date.toDateString());

      return true;
    },
    [isAuthenticated, authUser?.uid, setBirthDate],
  );

  const hasAlcoholOptIntBeenTriggered = useCallback(
    (productSubType: string, incrementCount: () => void) => {
      const isAlcoholEligibilityCheckRequired = isAlcoholOptInRequired(
        productSubType ?? '',
      );

      if (!isAlcoholEligibilityCheckRequired) {
        return false;
      }

      if (!userBirthDate?.length) {
        dispatchAlcoholEligibility({
          type: 'checkAlcoholEligibility',
          incrementProductCount: () => incrementCount(),
        });

        return true;
      }

      if (!isUserEligibleToBuyAlcohol()) {
        dispatchAlcoholEligibility({
          type: 'triggerIneligibilityToast',
        });

        return true;
      }

      return false;
    },
    [isAlcoholOptInRequired, isUserEligibleToBuyAlcohol, userBirthDate],
  );

  const memoizedContextValue = useMemo(
    () => ({
      updateUserBirthDate,
      alcoholEligibilityState,
      dispatchAlcoholEligibility,
      userBirthDate,
      hasAlcoholOptIntBeenTriggered,
      isUserEligibleToBuyAlcohol,
    }),
    [
      updateUserBirthDate,
      alcoholEligibilityState,
      dispatchAlcoholEligibility,
      userBirthDate,
      hasAlcoholOptIntBeenTriggered,
      isUserEligibleToBuyAlcohol,
      alcoholEligibilityState,
    ],
  );

  return (
    <AlcoholEligibilityContext.Provider value={memoizedContextValue}>
      {children}
    </AlcoholEligibilityContext.Provider>
  );
};

export const useAlcoholEligibilityContext = () => {
  const context = useContext(AlcoholEligibilityContext);

  if (!context) {
    throw new Error(
      'useAlcoholEligibilityContext must be used within a AlcoholEligibilityProvider',
    );
  }

  return context;
};
